import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiFileText } from "react-icons/fi";
import { SourcePreview } from "../../stream";
import styles from "./SourcePreviewList.module.css";

interface SourcePreviewListProps {
  /** Array of sources found during RAG retrieval */
  sources: SourcePreview[];
  /** Optional: Total number of chunks retrieved */
  totalChunks?: number;
  /** Optional: Number of organizations searched */
  orgsSearched?: number;
}

/**
 * Animation variants for staggered card appearance
 */
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: "easeOut",
    },
  },
};

/**
 * Format the preview text, truncating if necessary
 */
const formatPreview = (preview: string, maxLength: number = 150): string => {
  if (preview.length <= maxLength) {
    return preview;
  }
  return preview.slice(0, maxLength).trim() + "...";
};

/**
 * Get display name for org category
 */
const getCategoryLabel = (category: string): string => {
  switch (category) {
    case "internal":
      return "Internal";
    case "public":
      return "Public";
    case "personal":
      return "Personal";
    default:
      return category;
  }
};

/**
 * Individual source card component
 */
const SourceCard: React.FC<{ source: SourcePreview }> = ({ source }) => {
  return (
    <motion.div className={styles.sourceCard} variants={cardVariants}>
      <div className={styles.sourceHeader}>
        <span className={styles.documentName} title={source.document}>
          {source.document}
        </span>
        <span
          className={styles.orgBadge}
          data-category={source.org_category}
          title={source.org}
        >
          {getCategoryLabel(source.org_category)}
        </span>
      </div>
      {source.preview && (
        <p className={styles.preview}>{formatPreview(source.preview)}</p>
      )}
    </motion.div>
  );
};

/**
 * SourcePreviewList displays source documents found during RAG retrieval,
 * shown before the LLM generates the response.
 *
 * Features:
 * - Staggered fade-in animation for cards
 * - Category badges (Internal/Public/Personal)
 * - Preview text with truncation
 * - Summary of total chunks and orgs searched
 *
 * @example
 * ```tsx
 * <SourcePreviewList
 *   sources={[
 *     {
 *       org: "org-123",
 *       org_category: "internal",
 *       document: "Payroll Guide 2024.pdf",
 *       preview: "The payroll deadlines for Q1..."
 *     }
 *   ]}
 *   totalChunks={15}
 *   orgsSearched={2}
 * />
 * ```
 */
export const SourcePreviewList: React.FC<SourcePreviewListProps> = ({
  sources,
  totalChunks,
  orgsSearched,
}) => {
  if (sources.length === 0) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        className={styles.container}
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
      >
        {/* Header */}
        <div className={styles.header}>
          <FiFileText className={styles.headerIcon} />
          <span className={styles.headerText}>
            Finding relevant information from:
          </span>
        </div>

        {/* Source cards */}
        <motion.div className={styles.sourceList}>
          {sources.map((source, index) => (
            <SourceCard key={`${source.document}-${index}`} source={source} />
          ))}
        </motion.div>

        {/* Summary info */}
        {(totalChunks !== undefined || orgsSearched !== undefined) && (
          <div className={styles.moreInfo}>
            {totalChunks !== undefined && (
              <span>{totalChunks} relevant sections found</span>
            )}
            {totalChunks !== undefined && orgsSearched !== undefined && (
              <span> from </span>
            )}
            {orgsSearched !== undefined && (
              <span>
                {orgsSearched} knowledge {orgsSearched === 1 ? "base" : "bases"}
              </span>
            )}
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
};
